import { InviteIcon } from "@minket/brand/icons/invite";
import { TeamIcon } from "@minket/brand/icons/team";
import { canAssignRole, type Role } from "@minket/core";
import { Badge } from "@minket/ui/components/badge";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@minket/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import { useRecorded } from "@/components/audit/recorded";
import { stamp } from "@/components/ops/format";
import { When } from "@/components/ops/when";
import { UnavailableAction } from "@/components/page/action";
import { GhostTable } from "@/components/page/ghost";
import { Page } from "@/components/page/page";
import { OrgScope } from "@/components/page/scope";
import { Seams } from "@/components/page/seams";
import { Section, Sheet } from "@/components/page/section";
import { Notice, QueryNotice, QueryView } from "@/components/page/states";
import { RolesPlate } from "@/components/tenancy/roles";
import { endpoints, type ResponseOf } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiKey, apiQuery, apiRequest } from "@/lib/query";
import { displayName, roleLabels } from "@/lib/session";
import { notify } from "@/lib/toast";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/team")({
  component: Team,
});

type Member = ResponseOf<typeof endpoints.members>["data"][number];
type Invitation = ResponseOf<typeof endpoints.invitations>["data"][number];

const loadTeamDialogs = () => import("@/components/tenancy/team.ondemand");
const loadSign = () => import("@/components/tenancy/countersign.ondemand");
const InviteDialog = lazy(() => loadTeamDialogs().then((m) => ({ default: m.InviteDialog })));
const RoleDialog = lazy(() => loadTeamDialogs().then((m) => ({ default: m.RoleDialog })));
const Sign = lazy(() => loadSign().then((m) => ({ default: m.Sign })));

const columns = ["Member", "Role", "Joined, UTC"];
const ghost = () => (
  <GhostTable columns={columns} label="members" widths={[undefined, "9rem", "10rem"]} />
);

function Team() {
  const { organization, user } = useWorkspace();
  const orgId = organization?.organization_id ?? "";
  const actor: Role = organization?.role ?? "viewer";
  const canManage = useCan("members.manage");
  const queryClient = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const invite = useMutation({
    mutationFn: (body: { email: string; role: Role }) =>
      apiRequest(endpoints.addMember, { orgId }, { body }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.members, { orgId }) });
      void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.invitations, { orgId }) });
      setInviting(false);
      notify(
        "success",
        result.object === "member"
          ? `${result.user.email} is now a member.`
          : `Invitation saved. ${result.email} joins on their first sign-in.`,
      );
    },
  });
  const action = !organization ? undefined : canManage ? (
    <Button
      onClick={() => {
        invite.reset();
        setInviting(true);
      }}
      onPointerEnter={() => void loadTeamDialogs()}
      onFocus={() => void loadTeamDialogs()}
    >
      <InviteIcon size={20} aria-hidden />
      Invite member
    </Button>
  ) : (
    <UnavailableAction
      label="Invite member"
      icon={InviteIcon}
      reason="Admins and owners invite people"
    />
  );
  return (
    <Page title="Team" action={action}>
      <OrgScope
        will="Its members appear here with their roles."
        icon={<TeamIcon />}
        preview={ghost}
      >
        {(org) => (
          <Sheet>
            <MemberList orgId={org.organization_id} actor={actor} self={user.id} />
            <Section title="Roles" description="Admins hand out any role below owner.">
              <RolesPlate yours={organization?.role ?? null} />
            </Section>
            {canManage ? <Invitations orgId={org.organization_id} actor={actor} /> : null}
          </Sheet>
        )}
      </OrgScope>
      {inviting && organization ? (
        <Suspense fallback={null}>
          <InviteDialog
            organizationName={organization.name}
            actor={actor}
            invite={invite}
            onClose={() => setInviting(false)}
          />
        </Suspense>
      ) : null}
    </Page>
  );
}

function MemberList({ orgId, actor, self }: { orgId: string; actor: Role; self: string }) {
  const query = useQuery(apiQuery(endpoints.members, { orgId }));
  const queryClient = useQueryClient();
  const { user } = useWorkspace();
  const recorded = useRecorded();
  const [editing, setEditing] = useState<Member | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.members, { orgId }) });
  const change = useMutation({
    mutationFn: (role: Role) =>
      apiRequest(
        endpoints.updateMember,
        { orgId, memberId: editing?.id ?? "" },
        { body: { role } },
      ),
    onSuccess: (member) => {
      refresh();
      notify("success", `${member.user.email} is now ${roleLabels[member.role]}.`);
      setEditing(null);
    },
  });
  const remove = useMutation({
    mutationFn: (member: Member) =>
      apiRequest(endpoints.removeMember, { orgId, memberId: member.id }),
    onSuccess: (_, member) => {
      refresh();
      recorded(`${member.user.email} removed.`, { action: "member.remove" });
      setRemoving(null);
    },
  });
  return (
    <Section wide title="Members">
      <QueryView query={query} noun="members" preview={ghost}>
        {(page) =>
          page.data.length === 0 ? (
            <EmptyState
              data-state="empty"
              icon={<TeamIcon />}
              title="No members listed"
              description="An invited person becomes a member on their first sign-in."
              headingLevel={3}
              preview={ghost()}
            />
          ) : (
            <>
              <Table aria-label="Members">
                <TableHeader>
                  <TableRow>
                    {columns.map((column) => (
                      <TableHead
                        key={column}
                        className={column === "Joined, UTC" ? "text-right" : undefined}
                      >
                        {column}
                      </TableHead>
                    ))}
                    <TableHead className="w-0">
                      <span className="sr-only">Changes</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {page.data.map((member) => (
                    <TableRow key={member.id} className="hover:bg-transparent">
                      <TableCell className="min-w-56">
                        <span className="block font-semibold wrap-break-word">
                          {member.user.name ?? member.user.email}
                          {member.user.id === self ? (
                            <span className="font-normal text-muted-foreground"> (you)</span>
                          ) : null}
                        </span>
                        {member.user.name ? (
                          <span className="type-data block text-[12.5px] text-muted-foreground wrap-break-word">
                            <Seams>{member.user.email}</Seams>
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <Badge variant={member.role === "owner" ? "default" : "secondary"}>
                          {roleLabels[member.role]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <When value={member.created_at} />
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {canAssignRole(actor, member.role) ? (
                          <span className="inline-flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onPointerEnter={() => void loadTeamDialogs()}
                              onClick={() => {
                                change.reset();
                                setEditing(member);
                              }}
                            >
                              Change role
                              <span className="sr-only"> of {member.user.email}</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onPointerEnter={() => void loadSign()}
                              onClick={() => {
                                remove.reset();
                                setRemoving(member);
                              }}
                            >
                              Remove<span className="sr-only"> {member.user.email}</span>
                            </Button>
                          </span>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {editing ? (
                <Suspense fallback={null}>
                  <RoleDialog
                    member={{ name: editing.user.name ?? editing.user.email, role: editing.role }}
                    actor={actor}
                    change={change}
                    onClose={() => setEditing(null)}
                  />
                </Suspense>
              ) : null}
              {removing ? (
                <Suspense fallback={null}>
                  <Sign
                    title={`Remove ${removing.user.email}`}
                    description="They lose access to the organization from their next request. Invite them again to bring them back."
                    plan={{
                      label: "The member to remove",
                      columns: ["Action", "Member", "Joined"],
                      steps: [
                        {
                          key: removing.id,
                          verdict: "Remove",
                          subject: removing.user.name ?? removing.user.email,
                          detail: roleLabels[removing.role],
                          reason: `${stamp(removing.created_at)} UTC`,
                        },
                      ],
                    }}
                    act="Remove member"
                    tone="destructive"
                    pending={remove.isPending}
                    error={remove.error}
                    signer={displayName(user)}
                    onConfirm={() => remove.mutate(removing)}
                    onClose={() => setRemoving(null)}
                  />
                </Suspense>
              ) : null}
            </>
          )
        }
      </QueryView>
    </Section>
  );
}

function Invitations({ orgId, actor }: { orgId: string; actor: Role }) {
  const query = useQuery(apiQuery(endpoints.invitations, { orgId }));
  const queryClient = useQueryClient();
  const { user } = useWorkspace();
  const recorded = useRecorded();
  const [revoking, setRevoking] = useState<Invitation | null>(null);
  const revoke = useMutation({
    mutationFn: (invitation: Invitation) =>
      apiRequest(endpoints.revokeInvitation, { orgId, invitationId: invitation.id }),
    onSuccess: (_, invitation) => {
      void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.invitations, { orgId }) });
      recorded(`The invitation for ${invitation.email} is revoked.`, {
        action: "invitation.revoke",
      });
      setRevoking(null);
    },
  });
  const now = Date.now();
  return (
    <Section
      wide
      title="Invitations"
      description="Accepted on the first sign-in with the invited address, until they expire."
    >
      <QueryNotice query={query} noun="invitations">
        {(page) =>
          page.data.length === 0 ? (
            <Notice>No invitation is open.</Notice>
          ) : (
            <Table aria-label="Invitations">
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Expires, UTC</TableHead>
                  <TableHead className="w-0">
                    <span className="sr-only">Revoke</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {page.data.map((invitation) => (
                  <TableRow key={invitation.id} className="hover:bg-transparent">
                    <TableCell className="type-data min-w-56 wrap-break-word">
                      <Seams>{invitation.email}</Seams>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <Badge variant="secondary">{roleLabels[invitation.role]}</Badge>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {Date.parse(invitation.expires_at) < now ? (
                        <Badge variant="warning">Expired</Badge>
                      ) : (
                        <When value={invitation.expires_at} />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {canAssignRole(actor, invitation.role) ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onPointerEnter={() => void loadSign()}
                          onClick={() => {
                            revoke.reset();
                            setRevoking(invitation);
                          }}
                        >
                          Revoke<span className="sr-only"> invitation for {invitation.email}</span>
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        }
      </QueryNotice>
      {revoking ? (
        <Suspense fallback={null}>
          <Sign
            title={`Revoke the invitation for ${revoking.email}`}
            description="They do not join when they sign in. Invite them again at any time."
            plan={{
              label: "The invitation to revoke",
              columns: ["Action", "Address", "Expires"],
              steps: [
                {
                  key: revoking.id,
                  verdict: "Revoke",
                  subject: (
                    <code className="type-data">
                      <Seams>{revoking.email}</Seams>
                    </code>
                  ),
                  detail: roleLabels[revoking.role],
                  reason: `${stamp(revoking.expires_at)} UTC`,
                },
              ],
            }}
            act="Revoke invitation"
            tone="destructive"
            pending={revoke.isPending}
            error={revoke.error}
            signer={displayName(user)}
            onConfirm={() => revoke.mutate(revoking)}
            onClose={() => setRevoking(null)}
          />
        </Suspense>
      ) : null}
    </Section>
  );
}
